import { applyMiddleware, createStore, compose } from 'redux';
import logger from 'redux-logger';
import thunk from 'redux-thunk';
import promise from 'redux-promise-middleware';
import ws from '../utils/ws';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { webSocketMessageReceived } from 'actions';
import { composeWithDevTools } from 'redux-devtools-extension';
import rootReducer from 'reducers';

/**
 * Fill in expected fields on `_FULFILLED` payloads so reducers that assume
 * `payload.data.result` (and, for forms, `payload.data.validationMessages`) do
 * not crash when the API returns a thin body — e.g. an event data set that has
 * not been loaded yet.
 *
 * Centralised here to avoid null-guarding boilerplate in every reducer.
 */
const isPlainObject = value =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const ensureFulfilledPayloadDataMiddleware = () => next => action => {
  if (action && typeof action.type === 'string' && action.type.endsWith('_FULFILLED')) {
    const payload = action.payload ?? {};

    // Only fill when missing (don't coerce real non-object payloads like strings/arrays).
    if (payload.data == null) payload.data = {};

    // Many reducers assume `payload.data.result` exists; `FIND_*` endpoints
    // expect an array, the rest an object.
    if (payload.data != null && typeof payload.data === 'object' && payload.data.result == null) {
      payload.data.result = action.type.startsWith('FIND_') ? [] : {};
    }

    // Many forms assume `payload.data.validationMessages` is an object.
    if (payload.data != null && typeof payload.data === 'object') {
      if (!isPlainObject(payload.data.validationMessages)) payload.data.validationMessages = {};
    }

    return next({ ...action, payload });
  }
  return next(action);
};

export default function configureStore(preloadedState = {}) {
  const middlewares = [promise, ensureFulfilledPayloadDataMiddleware, thunk, logger];
  const middlewareEnhancer = composeWithDevTools(
    applyMiddleware(...middlewares)
  );

  const enhancers = [middlewareEnhancer];
  const composedEnhancers = compose(...enhancers);

  const rws = new ReconnectingWebSocket(ws.webSocketUrl(), [], {
    minReconnectionDelay: 1000,
    reconnectionDelayGrowFactor: 1
  });

  rws.addEventListener('open', () => {});

  rws.addEventListener('message', event => {
    store.dispatch(webSocketMessageReceived(event.data));
  });

  const store = createStore(rootReducer, preloadedState, composedEnhancers);

  return store;
}
